import { useEffect, useRef } from "react";

import { LAP, tour, type FigureMount, type Readout, type Tour } from "./stage";
import { inject } from "./styles";

import { cn } from "@/components/ui/utils";

/**
 * A figure drawn as ``@lucasmarkes/hairline`` draws one: an isometric line
 * drawing that answers the pointer, on the package's own engine (the vendored
 * ``iso`` / ``motion`` / ``stage`` / ``styles``). The landing page's figures
 * (``abacus.ts``, ``ledger.ts``, ``shelf.ts``) were made with its
 * hairline-create skill, and each declares what this needs to mount it.
 */
export type HairlineFigure = {
  name: string;
  /** One sentence saying what the figure shows: its accessible name. */
  means: string;
  /** The figure's own number at intensity 0, 0.5 and 1: it is mounted at the middle one. */
  range: readonly [number, number, number];
  /** Where the unseen pointer stops while the figure plays; null leaves the stage. */
  tour: Tour | null;
  mount: FigureMount;
};

/**
 * One figure, mounted on its stage the way the package's ``create`` mounts
 * one: its svg, its read-out, and, while ``isPlaying``, an unseen pointer
 * walking its tour until a real pointer or focus arrives. Its strokes are the
 * app's neutrals and its plates the page's ground, in either scheme.
 */
export function Hairline({
  figure,
  isPlaying = false,
  onRead,
  className,
}: {
  figure: HairlineFigure;
  isPlaying?: boolean;
  /** The figure's caption, each time it changes: what is under the pointer, or ``rest``. */
  onRead?: (text: string) => void;
  className?: string;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const onReadRef = useRef(onRead);

  useEffect(() => {
    onReadRef.current = onRead;
  });

  useEffect(() => {
    const stage = stageRef.current;
    inject(document);
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 400 320");
    svg.setAttribute("aria-hidden", "true");
    stage.appendChild(svg);

    let text: string | null = null;
    const read: Readout = {
      get textContent() {
        return text;
      },
      set textContent(value) {
        const next = value ?? "";
        if (next === text) return;
        text = next;
        onReadRef.current?.(next);
      },
    };

    // The tour registers before the engine, so its board ticks first in a
    // frame: the unseen pointer moves, then the figure draws its answer.
    const playing = isPlaying ? tour(stage, figure.tour ?? LAP) : null;
    const engine = figure.mount({ stage, svg, read }, figure.range[1]);
    if (text === null) read.textContent = "rest";

    return () => {
      playing?.stop();
      engine.destroy();
      svg.remove();
    };
  }, [figure, isPlaying]);

  return (
    <div
      ref={stageRef}
      data-hairline={figure.name}
      role="img"
      aria-label={figure.means}
      className={cn(
        "[--hairline-plate:var(--background)] [--hairline-hi:var(--color-neutral-800)] [--hairline-edge:var(--color-neutral-300)] [--hairline-mid:var(--color-neutral-200)] [--hairline-lo:var(--color-neutral-100)]",
        "dark:[--hairline-hi:var(--color-neutral-100)] dark:[--hairline-edge:var(--color-neutral-600)] dark:[--hairline-mid:var(--color-neutral-700)] dark:[--hairline-lo:var(--color-neutral-800)]",
        className,
      )}
    />
  );
}
